import { forwardRef, useId } from 'react'

import styles from './Input.module.css'

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  error?: string
  hint?: string
  size?: 'sm' | 'md' | 'lg'
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  isFullWidth?: boolean
  disabledAppearance?: 'default' | 'muted'
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      size = 'md',
      leftIcon,
      rightIcon,
      isFullWidth = false,
      disabledAppearance = 'default',
      className,
      id: providedId,
      disabled = false,
      ...props
    },
    ref
  ) => {
    const generatedId = useId()
    const id = providedId ?? generatedId

    const containerClasses = [styles.container, isFullWidth && styles.fullWidth, className]
      .filter(Boolean)
      .join(' ')

    const inputWrapperClasses = [
      styles.inputWrapper,
      styles[size],
      error && styles.hasError,
      leftIcon && styles.hasLeftIcon,
      rightIcon && styles.hasRightIcon,
      disabled && disabledAppearance === 'muted' && styles.disabledMuted,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div className={containerClasses}>
        {label && (
          <label htmlFor={id} className={styles.label}>
            {label}
          </label>
        )}
        <div className={inputWrapperClasses}>
          {leftIcon && <span className={styles.leftIcon}>{leftIcon}</span>}
          <input ref={ref} id={id} className={styles.input} disabled={disabled} {...props} />
          {rightIcon && <span className={styles.rightIcon}>{rightIcon}</span>}
        </div>
        {error && <p className={styles.error}>{error}</p>}
        {hint && !error && <p className={styles.hint}>{hint}</p>}
      </div>
    )
  }
)

Input.displayName = 'Input'
